import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { adminApi } from '../../api/admin/adminApi';
import styles from './AdminOperations.module.css';

const STATE_FILTERS = [
  { value: '', label: 'All states' },
  { value: 'reserved', label: 'Activation pending' },
  { value: 'active', label: 'Linked' },
  { value: 'revoked', label: 'Inactive' },
  { value: 'missing', label: 'Not in ledger' },
];

function money(amount, currency = 'GBP') {
  if (amount == null) return '—';
  try {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(amount);
  } catch {
    return `${Number(amount).toFixed(2)} ${currency}`;
  }
}

function titleCase(value) {
  if (!value) return '—';
  return String(value).replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function shortId(value) {
  const text = String(value || '');
  if (text.length <= 14) return text || '—';
  return `${text.slice(0, 6)}…${text.slice(-4)}`;
}

function when(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }).format(date);
}

function toneFor(order) {
  if (order.cardStatus === 'active') return styles.good;
  if (order.cardStatus === 'reserved') return styles.warn;
  if (order.cardStatus === 'revoked') return styles.bad;
  const payment = String(order.financialStatus || '');
  if (payment === 'refunded' || payment === 'voided') return styles.bad;
  if (payment === 'paid') return styles.good;
  if (payment === 'pending' || payment === 'authorized') return styles.warn;
  return styles.neutral;
}

function paymentTone(status) {
  if (status === 'paid') return styles.good;
  if (status === 'pending' || status === 'authorized' || status === 'partially_paid') return styles.warn;
  if (status === 'refunded' || status === 'voided' || status === 'partially_refunded') return styles.bad;
  return styles.neutral;
}

function Kpi({ label, value, hint }) {
  return (
    <div className={styles.kpi}>
      <p className={styles.kpiLabel}>{label}</p>
      <p className={styles.kpiValue}>{value}</p>
      {hint ? <p className={styles.kpiHint}>{hint}</p> : null}
    </div>
  );
}

export default function AdminOrdersPage() {
  const { registerShellMeta, setRefreshHandler } = useOutletContext() || {};
  const [orders, setOrders] = useState([]);
  const [commercial, setCommercial] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [payment, setPayment] = useState('');
  const [state, setState] = useState('');
  const [selectedId, setSelectedId] = useState(null);

  const load = useCallback(async () => {
    setError('');
    try {
      const res = await adminApi.listIndividualOrders();
      setOrders(res.data?.orders || []);
      setCommercial(res.data?.commercial || null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    registerShellMeta?.({ title: 'Orders', subtitle: 'Individual Shopify purchases' });
    setRefreshHandler?.(load);
    load();
  }, [load, registerShellMeta, setRefreshHandler]);

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return orders.filter((order) => {
      if (payment && order.financialStatus !== payment) return false;
      if (state === 'missing' && order.cardStatus) return false;
      if (state && state !== 'missing' && order.cardStatus !== state) return false;
      if (!needle) return true;
      return [order.orderId, order.orderName, order.email, order.cardId, order.assignedAccount?.email]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(needle));
    });
  }, [orders, payment, query, state]);

  const selected = visible.find((order) => order.orderId === selectedId) || null;
  const currency = commercial?.currency || 'GBP';

  return (
    <div className={styles.page}>
      <section className={styles.kpis} aria-label="Order totals">
        <Kpi label="Orders today" value={commercial ? commercial.ordersToday : '—'} />
        <Kpi label="Revenue today" value={commercial ? money(commercial.revenueToday, currency) : '—'} hint="Gross" />
        <Kpi label="Total orders" value={commercial ? commercial.totalOrders : '—'} hint="All time" />
        <Kpi label="Total revenue" value={commercial ? money(commercial.totalRevenue, currency) : '—'} hint="Gross" />
        <Kpi label="7-day revenue" value={commercial ? money(commercial.revenue7d, currency) : '—'} hint="Gross" />
        <Kpi label="30-day revenue" value={commercial ? money(commercial.revenue30d, currency) : '—'} hint="Gross" />
      </section>
      {commercial?.incomplete ? (
        <p className={styles.notice}>
          {commercial.unpricedOrders} stored {commercial.unpricedOrders === 1 ? 'order has' : 'orders have'} no amount, so revenue is incomplete.
        </p>
      ) : null}
      {commercial?.mixedCurrency ? (
        <p className={styles.notice}>Orders use more than one currency, so those totals are not combined.</p>
      ) : null}
      {error ? <p className={styles.error} role="alert">{error}</p> : null}

      <div className={styles.toolbar}>
        <input
          className={styles.search}
          value={query}
          placeholder="Search order, purchaser, card"
          aria-label="Search orders"
          onChange={(event) => setQuery(event.target.value)}
        />
        <label className={styles.field}>
          Payment
          <select aria-label="Payment" value={payment} onChange={(event) => setPayment(event.target.value)}>
            <option value="">All payments</option>
            <option value="paid">Paid</option>
            <option value="pending">Pending</option>
            <option value="authorized">Authorised</option>
            <option value="refunded">Refunded</option>
            <option value="partially_refunded">Partially refunded</option>
          </select>
        </label>
        <label className={styles.field}>
          Card
          <select aria-label="Card state" value={state} onChange={(event) => setState(event.target.value)}>
            {STATE_FILTERS.map((option) => (
              <option key={option.value || 'all'} value={option.value}>{option.label}</option>
            ))}
          </select>
        </label>
      </div>

      {loading ? <p className={styles.loading}>Loading orders…</p> : null}
      {!loading && visible.length === 0 ? <p className={styles.empty}>No orders match this view.</p> : null}

      {visible.length > 0 ? (
        <div className={styles.list}>
          <div className={`${styles.head} ${styles.orders}`}>
            <span>Order</span>
            <span>Purchaser</span>
            <span>Payment</span>
            <span>Amount</span>
            <span>Card</span>
            <span>State</span>
            <span>Account</span>
            <span>Created</span>
          </div>
          {visible.map((order) => (
            <button
              key={order.orderId}
              type="button"
              className={`${styles.row} ${styles.orders}`}
              aria-current={selectedId === order.orderId ? 'true' : undefined}
              onClick={() => setSelectedId(order.orderId)}
            >
              <span className={`${styles.cell} ${styles.wide}`} data-label="Order">
                <span className={styles.mono}>{order.orderName || order.orderId}</span>
                {order.orderName ? <span className={styles.sub}>{order.orderId}</span> : null}
              </span>
              <span className={styles.cell} data-label="Purchaser">{order.email}</span>
              <span className={styles.cell} data-label="Payment">
                <span className={`${styles.badge} ${paymentTone(order.financialStatus)}`}>{titleCase(order.financialStatus)}</span>
              </span>
              <span className={styles.cell} data-label="Amount">{money(order.amount, order.currency || currency)}</span>
              <span className={`${styles.cell} ${styles.mono}`} data-label="Card" title={order.cardId}>{shortId(order.cardId)}</span>
              <span className={styles.cell} data-label="State">
                <span className={`${styles.badge} ${toneFor(order)}`}>{order.displayState}</span>
              </span>
              <span className={styles.cell} data-label="Account">{order.assignedAccount?.email || '—'}</span>
              <span className={styles.cell} data-label="Created">{when(order.createdAt)}</span>
            </button>
          ))}
        </div>
      ) : null}

      {selected ? (
        <div className="admin-drawer-overlay" onClick={() => setSelectedId(null)} role="presentation">
          <div
            className="admin-drawer"
            role="dialog"
            aria-label={`Order ${selected.orderName || selected.orderId}`}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="admin-drawer-header">
              <h2>{selected.orderName || selected.orderId}</h2>
              <button type="button" className="admin-drawer-close" onClick={() => setSelectedId(null)} aria-label="Close">×</button>
            </div>
            <div className="admin-drawer-body">
              <dl className={styles.facts}>
                <dt>Purchaser</dt>
                <dd>{selected.email}</dd>
                <dt>Payment</dt>
                <dd>{titleCase(selected.financialStatus)}</dd>
                <dt>Fulfilment</dt>
                <dd>{titleCase(selected.fulfillmentStatus)}</dd>
                <dt>Amount</dt>
                <dd>{money(selected.amount, selected.currency || currency)}</dd>
                <dt>Card</dt>
                <dd className={styles.mono}>{selected.cardId}</dd>
                <dt>State</dt>
                <dd>{selected.displayState}</dd>
                <dt>Account</dt>
                <dd>{selected.assignedAccount?.email || '—'}</dd>
                <dt>Programmed</dt>
                <dd>{selected.programmed == null ? '—' : selected.programmed ? 'Yes' : 'No'}</dd>
                <dt>Created</dt>
                <dd>{selected.createdAt ? new Date(selected.createdAt).toLocaleString('en-GB') : '—'}</dd>
              </dl>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
